// RoseWidget - Main chat widget component
// This is the core Rose Widget application that handles the chat interface,
// including chat messages, streaming, and evaluation.

declare global {
    interface Window {
        __InboundXBarVisible?: boolean;
        __InboundXWidgetReady?: boolean;
    }
}

import type React from 'react';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { NetworkAdapter } from '../adapters/network';
import type { StorageAdapter } from '../adapters/storage';
import {
    Analytics,
    getAnalyticsContext,
    hasPostHogInitializedEventBeenSent,
    setAnalyticsContext,
    track,
} from '../analytics';
import { getCrossDomainTrackingData } from '../analytics/cross-domain-tracker';
import type { MessageSourceType } from '../analytics/types';
import {
    type BrowserRevealData,
    captureWindowReveal,
    injectRevealSimulationIfEnabled,
} from '../browserReveal';
import {
    type ExpandedLayout,
    FORM_ASSISTANT_WELCOME_MARKER,
    type FormFactor,
    getDefaultApiUrl,
    getFormAssistantInitialMessage,
    POST_CONVERSION_WELCOME_MARKER,
} from '../config/constants';
import { getDeploymentTarget, isBackofficePlayground } from '../config/deployment';
import {
    getActivePostConversionForm,
    getFormAssistantQuestions,
} from '../config/SiteConfigManager';
import { useConfigResolver } from '../config/unified/ConfigProvider';
import { useAnalytics } from '../context/AnalyticsContext';
import { ChatThemeProvider } from '../context/ChatThemeContext';
import { useEvaluation } from '../hooks/chat/useEvaluation';
import { useStreamingMessage } from '../hooks/chat/useStreamingMessage';
import { ensureSnitcherRadarReady, useSnitcherRadar } from '../hooks/useSnitcherRadar';
import { useVector } from '../hooks/useVector';
import { useConversationFlowReducer } from '../hooks/widget/useConversationFlowReducer';
import { useScrollBehavior } from '../hooks/widget/useScrollBehavior';
import { useServiceInitialization } from '../hooks/widget/useServiceInitialization';
import { useWidgetReducer } from '../hooks/widget/useWidgetReducer';
import {
    type AssistantMessage,
    ChatStorageService,
    createAssistantMessage,
    createConversationPair,
    createFormAssistantWelcomePair,
    createUserMessage,
    type EvaluationRating,
    isAssistantMessage,
} from '../services/chatStorage';
import { conversationScope } from '../services/conversationScope';
import type { EvaluationSubmissionOverride } from '../services/evaluation';
import { useIdentificationAllowed } from '../services/identificationConsent';
import type { BookingContext } from '../services/inboundx';
import { InboundXService } from '../services/inboundx';
import { roseStorage } from '../services/rose-storage';
import { getUserLanguage, getUserLocale } from '../utils/content/languageUtils';
import { getMessage } from '../utils/content/messageTranslations';
import { logger } from '../utils/core/logger';
import { getDeviceType } from '../utils/device/mobileDetection';
import { getStableClientId } from '../utils/domain/clientId';
import { resolvePageDisplayRule } from '../utils/domain/pageDisplayRules';
import { buildWidgetThemeVars } from '../utils/ui/widgetTheme';
import { updateCurrentPageRoseAttribution } from '../utils/web/currentPageRoseAttribution';
import { trackConversation } from '../utils/widget/conversationUtils';
import { shouldResetConversationForPostConversion } from '../utils/widget/postConversion';
import { resolveExpandedLayout } from '../utils/widget/viewLayout';
import {
    FULLSCREEN_TAKEOVER_Z_INDEX,
    resolveApiUrl,
    resolveZIndex,
} from '../utils/widget/widgetConfig';
import CollapsedChatView from './CollapsedChatView';
import EvaluationPopup, { type EvaluationPopupResult } from './EvaluationPopup';
import ExpandedChatView from './ExpandedChatView';
import { HiddenZoneButton } from './HiddenZoneButton';
import MinimizedChatView from './MinimizedChatView';

export interface RoseWidgetRef {
    collapse: () => void;
    expand: () => void;
    minimize: () => void;
    reset: () => void;
    getState: () => 'collapsed' | 'expanded' | 'minimized';
    isExpanded: boolean;
    isExplicitlyMinimized: boolean;
}

export interface RoseWidgetProps {
    onClose?: () => void;
    apiUrl?: string;
    apiKey?: string;
    /**
     * When true, apiUrl prop takes absolute precedence over backoffice api_endpoint config.
     * Used by playground when a custom endpoint (e.g. localhost) is explicitly set.
     */
    forceApiUrl?: boolean;
    siteName?: string;
    domain?: string;
    storageAdapter?: StorageAdapter;
    networkAdapter?: NetworkAdapter;
    forceDisplay?: boolean;
    enableEvaluation?: boolean;
    submitEvaluationOverride?: EvaluationSubmissionOverride;
    conversationLanguage?: string;
    visitorIp?: string;
    /**
     * Browser Reveal data (optional, will auto-detect if not provided)
     * This is NOT from a Reveal API - just window.reveal browser variable
     */
    browserRevealData?: BrowserRevealData;
    /**
     * Display mode for the widget
     * - 'normal': Widget is immediately visible (default)
     * - 'hidden_zone': Widget starts as hidden button, user must click to reveal
     *
     * Note: 'minimized' display is handled internally via page_display_rules resolution.
     * RoseWidget resolves page rules from siteConfig + currentUrl to set isExplicitlyMinimized.
     */
    displayMode?: 'normal' | 'hidden_zone';
    /**
     * Current page URL for page_display_rules resolution.
     * When provided, RoseWidget resolves page rules internally (hidden/minimized/displayed).
     * Both widget DisplayController and chrome extension pass this from their own URL tracking.
     */
    currentUrl?: string;
    /**
     * Container element for rendering hidden zone button outside shadow DOM
     * Required for proper viewport positioning when displayMode is 'hidden_zone'
     */
    hiddenZoneContainer?: HTMLElement;
    /**
     * Form factor for the widget layout and positioning
     * - 'default': Centered bottom positioning (standard chat widget)
     * - 'copilot': Right-side panel positioning (assistant mode)
     */
    formFactor?: FormFactor;
    /**
     * Whether form assistant behavior is enabled
     * When true, shows the welcome message with form-related dynamic questions
     * This is separate from formFactor to allow copilot UI without form assistant behavior
     */
    isFormAssistant?: boolean;
    /**
     * Whether post-conversion qualification mode is active.
     * When true, auto-sends a trigger message to start the qualification flow.
     */
    isPostConversion?: boolean;
    /**
     * Playground device-preview only: simulated viewport width used for the widget's
     * JS width breakpoints (mobile full-height layout). Undefined in production.
     */
    previewViewportWidth?: number;
}

const RoseWidget = forwardRef<RoseWidgetRef, RoseWidgetProps>(
    (
        {
            onClose: _onClose,
            apiUrl = getDefaultApiUrl(),
            apiKey: _apiKey,
            forceApiUrl = false,
            siteName = 'Example',
            domain = 'example.com',
            storageAdapter,
            networkAdapter,
            forceDisplay = false,
            enableEvaluation = false,
            submitEvaluationOverride,
            conversationLanguage,
            visitorIp,
            browserRevealData,
            displayMode = 'normal',
            currentUrl: currentUrlProp,
            hiddenZoneContainer,
            formFactor: formFactorProp = 'default',
            isFormAssistant = false,
            isPostConversion = false,
            previewViewportWidth,
        },
        ref
    ) => {
        const isForcedBackofficePreview = forceDisplay && isBackofficePlayground();
        // Allow exiting URL-triggered copilot mode (curated content links).
        // Form assistant copilot is permanent and cannot be overridden.
        const [formFactorOverride, setFormFactorOverride] = useState<FormFactor | null>(null);
        const formFactor = formFactorOverride ?? formFactorProp;

        const initialViewLayout = useMemo(() => ChatStorageService.getViewLayout(domain), [domain]);

        // Widget state management with useReducer
        const {
            state: widgetState,
            actions: widgetActions,
            getState,
        } = useWidgetReducer({
            viewLayout: initialViewLayout,
        });

        // Conversation flow state management (booking, redirecting, answering + visitor profile)
        const { flowState, actions: flowActions } = useConversationFlowReducer({ isFormAssistant });

        // Scroll behavior hook
        const { messagesEndRef, latestQuestionRef, scrollToBottom, scrollToLatestQuestion } =
            useScrollBehavior();

        // Refs for message tracking
        const [_isProgrammaticallySending, setIsProgrammaticallySending] = useState(false);
        const currentMessageSourceRef = useRef<MessageSourceType>('user_typed');
        const conversationTriggeredByRef = useRef<MessageSourceType | null>(null);
        const userMessageCountRef = useRef(0);
        // IX-4266: guards handleResetConversation against overlapping runs.
        const isResettingRef = useRef(false);
        const isRetryingRef = useRef(false);
        const hasPreparedSnitcherForIdentificationRef = useRef(false);
        const hasAppliedCopilotInitialState = useRef(false);
        const hasAppliedPostConversionAutoOpen = useRef(false);
        const latestProfilingProgressRef = useRef<{
            current_question: number;
            total_fields: number;
            is_complete: boolean;
        } | null>(null);
        const pageRuleAppliedForUrl = useRef<string | null>(null);
        const displayedRuleAppliedForUrl = useRef<string | null>(null);
        const [streamingEnabled] = useState(true);

        // Track booking state in a ref so streaming completion callbacks always see the latest value
        // (closures captured at streaming start would be stale)
        const isBookingRef = useRef(flowState.isBooking);
        isBookingRef.current = flowState.isBooking;

        // Ref to store handleSendMessage for use in useEffect (defined after conditional returns)
        const handleSendMessageRef = useRef<
            | ((
                  message: string,
                  source?: MessageSourceType,
                  bookingContext?: BookingContext,
                  questionContext?: string
              ) => void)
            | null
        >(null);

        // Visitor profile is managed inside flowState

        // Track if an AI Section's search bar is visible (for hidesMainBar feature)
        // When true, this widget's main search bar should be hidden
        const [sectionSearchBarVisible, setSectionSearchBarVisible] = useState(false);

        const configResolver = useConfigResolver();
        const enableCopilotLayout = useMemo(() => {
            // Preprod override takes precedence over config
            try {
                const override = localStorage.getItem('rose_preprod_copilot_layout_override');
                if (override === 'true') return true;
                if (override === 'false') return false;
            } catch {
                /* ignore */
            }
            return configResolver.appearance.enable_copilot_layout ?? false;
        }, [configResolver]);

        const effectiveViewLayout = useMemo<ExpandedLayout>(
            () =>
                resolveExpandedLayout({
                    formFactor,
                    isFormAssistant,
                    storedLayout: widgetState.viewLayout,
                    enableCopilotLayout,
                }),
            [enableCopilotLayout, formFactor, isFormAssistant, widgetState.viewLayout]
        );

        useEffect(() => {
            if (formFactor !== 'copilot' && !enableCopilotLayout) {
                ChatStorageService.setViewLayout(domain, 'expanded');
                widgetActions.setViewLayout('expanded');
            }
        }, [domain, enableCopilotLayout, formFactor, widgetActions]);

        // Get analytics ready state from context
        const { isAnalyticsReady } = useAnalytics();

        // When config sets llm_response_language to "auto", pass "auto" to the service
        // so the LLM detects the visitor's language. UI elements still use the site language.
        const effectiveConversationLanguage = useMemo(() => {
            if (conversationLanguage) return conversationLanguage; // explicit prop (playground)
            if (configResolver.identity.llm_response_language === 'auto') return 'auto'; // config says auto
            return undefined; // normal behavior — service uses detected site language
        }, [conversationLanguage, configResolver.identity.llm_response_language]);

        const primaryColor = useMemo(
            () => configResolver.appearance.brand_color ?? '#0A42C3',
            [configResolver.appearance.brand_color]
        );
        const themeVars = useMemo(
            () => buildWidgetThemeVars(configResolver.appearance),
            [configResolver.appearance]
        );
        const zIndex = useMemo(
            () => resolveZIndex(configResolver.appearance.z_index),
            [configResolver.appearance.z_index]
        );

        // Compute bar visibility from reducer state (single source of truth)
        // This represents widget READINESS (for signaling to AI Sections), not visual state.
        // Visual hiding due to sectionSearchBarVisible is handled separately via
        // the hiddenBySectionSearchBar prop on CollapsedChatView.
        const isBarVisible = useMemo(() => {
            if (displayMode === 'hidden_zone') {
                return widgetState.userClickedHiddenZone;
            }
            return true; // Normal mode is always visible
        }, [displayMode, widgetState.userClickedHiddenZone]);

        // Signal bar visibility to external integrations (AI Sections, etc.)
        // This effect handles BOTH:
        // 1. Push notifications: dispatch events when visibility changes
        // 2. Pull requests: respond to visibility requests from AI Sections
        useEffect(() => {
            // Helper to dispatch visibility events
            const signalVisible = () => {
                logger.debug('RoseWidget: Signaling bar VISIBLE to external integrations');
                window.__InboundXBarVisible = true;
                window.dispatchEvent(new CustomEvent('inboundx_bar_visible'));
            };

            const signalHidden = () => {
                logger.debug('RoseWidget: Signaling bar HIDDEN to external integrations');
                window.__InboundXBarVisible = false;
                window.dispatchEvent(new CustomEvent('inboundx_bar_hidden'));
            };

            // Handle visibility requests from AI Sections (pull mechanism)
            const handleVisibilityRequest = () => {
                logger.debug('RoseWidget: Received visibility request, responding with:', {
                    isBarVisible,
                });
                if (isBarVisible) {
                    signalVisible();
                } else {
                    signalHidden();
                }
            };

            // Listen for visibility requests
            window.addEventListener('inboundx_request_visibility', handleVisibilityRequest);

            // Push current visibility state (on mount or when visibility changes)
            if (isBarVisible) {
                signalVisible();
            } else {
                signalHidden();
            }

            // Cleanup: signal hidden and remove listener
            return () => {
                window.removeEventListener('inboundx_request_visibility', handleVisibilityRequest);
                signalHidden();
            };
        }, [isBarVisible]);

        // Listen for AI Section search bar visibility events (hidesMainBar feature)
        // When an AI Section's search bar enters the viewport, hide this widget's main bar
        useEffect(() => {
            const handleSectionSearchBarEntered = () => {
                logger.debug('RoseWidget: AI Section search bar entered viewport, hiding main bar');
                setSectionSearchBarVisible(true);
            };

            const handleSectionSearchBarExited = () => {
                logger.debug('RoseWidget: AI Section search bar exited viewport, showing main bar');
                setSectionSearchBarVisible(false);
            };

            window.addEventListener(
                'inboundx_section_searchbar_entered',
                handleSectionSearchBarEntered
            );
            window.addEventListener(
                'inboundx_section_searchbar_exited',
                handleSectionSearchBarExited
            );

            return () => {
                window.removeEventListener(
                    'inboundx_section_searchbar_entered',
                    handleSectionSearchBarEntered
                );
                window.removeEventListener(
                    'inboundx_section_searchbar_exited',
                    handleSectionSearchBarExited
                );
            };
        }, []);

        // Bypass scroll detection when forceDisplay is enabled (e.g., demo pages)
        // Preprod pages will use scroll detection for testing
        const bypassScrollDetection = forceDisplay;

        const siteConfig = useMemo(
            () => ({
                name: configResolver.identity.company_name || siteName,
            }),
            [configResolver.identity.company_name, siteName]
        );

        // Determine effective API URL using utility function
        // When forceApiUrl is true (playground custom endpoint), skip backoffice override
        const effectiveApiUrl = useMemo(
            () =>
                forceApiUrl
                    ? apiUrl
                    : resolveApiUrl(configResolver.system.api_endpoint, apiUrl, domain),
            [forceApiUrl, configResolver.system.api_endpoint, apiUrl, domain]
        );

        // Sites with identification_requires_consent identify nobody until the host's
        // cookie banner relays consent through setIdentificationConsent(true) (IX-5465).
        const identificationAllowed = useIdentificationAllowed(
            configResolver.qualification?.identification_requires_consent
        );

        const prepareVisitorIdentification = useCallback(() => {
            if (!identificationAllowed || hasPreparedSnitcherForIdentificationRef.current) {
                return;
            }

            hasPreparedSnitcherForIdentificationRef.current = true;
            void ensureSnitcherRadarReady(effectiveApiUrl);
        }, [effectiveApiUrl, identificationAllowed]);

        // Auto-capture browser Reveal data if not provided
        const [capturedRevealData, setCapturedRevealData] = useState<BrowserRevealData | undefined>(
            browserRevealData
        );

        useEffect(() => {
            // Only auto-capture if not provided via props
            if (!browserRevealData && !capturedRevealData) {
                // First, inject simulation data if enabled (playground testing)
                injectRevealSimulationIfEnabled();

                // Then capture whatever is on window.reveal
                const reveal = captureWindowReveal();
                if (reveal) {
                    logger.debug('[RoseWidget] Auto-captured browser Reveal data');
                    setCapturedRevealData(reveal);
                }
            }
        }, [browserRevealData, capturedRevealData]);

        // Use provided browserRevealData or auto-captured data
        const effectiveBrowserRevealData = browserRevealData || capturedRevealData;

        // Initialize services and state - MUST be called before any conditional returns
        const {
            chatService,
            evaluationService,
            currentSessionId,
            storageScope,
            isInitialized,
            isApiCompatibleAndAvailable,
            versionCheckError: _versionCheckError,
            evaluationEnabled,
            relatedEnabled: _relatedEnabled,
            conversationPairs,
            isExpanded,
            isExplicitlyMinimized,
            // apiVersion and environment now set in AnalyticsContext, not needed here
            setupEvaluationPopupCallback,
            setState,
        } = useServiceInitialization({
            domain,
            apiUrl: effectiveApiUrl,
            storageAdapter,
            networkAdapter,
            isSiteConfigLoaded: true,
            isAnalyticsReady,
            enableEvaluation,
            conversationLanguage: effectiveConversationLanguage,
            visitorIp,
            browserRevealData: effectiveBrowserRevealData,
        });

        // Update shadow host z-index when config loads or the widget enters/exits
        // a full-screen takeover.
        //
        // Shadow host z-index controls widget position relative to page elements
        // (e.g. cookie/promo banners). The configured z-index intentionally lets
        // those banners sit on top of the collapsed bubble and the desktop panel.
        // But when the expanded chat takes over the full mobile viewport, its
        // close button sits at the top where a banner can occlude it, leaving the
        // user unable to close Rose (IX-3375). In that state only, raise the host
        // above page content. The 1242px threshold mirrors the full-height layout
        // switch in ExpandedChatView. `isExpanded` here is the same render-driving
        // value as the expanded view (from useServiceInitialization), so the host
        // is lowered back as soon as the chat is minimized. A resize listener
        // re-applies the z-index when the viewport crosses the 1242px threshold
        // while expanded (e.g. DevTools device-simulation toggles), mirroring how
        // ExpandedChatView reacts to resize.
        useEffect(() => {
            const applyZIndex = () => {
                // Find the shadow host by looking for elements with id starting with 'inboundx-widget-'
                const shadowHost = Array.from(
                    document.querySelectorAll('[id^="inboundx-widget-"]')
                ).find((el) => el.shadowRoot !== null) as HTMLElement;
                if (!shadowHost) {
                    return;
                }

                const isFullScreenTakeover =
                    isExpanded &&
                    formFactor !== 'copilot' &&
                    effectiveViewLayout !== 'copilot' &&
                    window.innerWidth <= 1242;
                const targetZIndex = isFullScreenTakeover ? FULLSCREEN_TAKEOVER_Z_INDEX : zIndex;

                if (shadowHost.style.zIndex !== String(targetZIndex)) {
                    shadowHost.style.zIndex = String(targetZIndex);
                    logger.debug(
                        `✅ Updated shadow host z-index to ${targetZIndex}` +
                            (isFullScreenTakeover ? ' (full-screen takeover)' : ' from config')
                    );
                }
            };

            applyZIndex();
            window.addEventListener('resize', applyZIndex);
            return () => window.removeEventListener('resize', applyZIndex);
        }, [zIndex, isExpanded, formFactor, effectiveViewLayout]);

        // Identify visitors via paid third-party enrichment (Snitcher, Vector)
        // either for everyone when "identify all visitors" (enrich_all_visitors)
        // is on, or — when off — only once the visitor has engaged (sent >=1
        // message), so we don't pay vendors for anonymous bounce traffic.
        // conversationPairs is reactive, so this flips false -> true on the first
        // message and re-runs the gated hooks' effects, loading the scripts then.
        // On consent-gated sites both cases also wait for the host's consent.
        const shouldIdentifyVisitor =
            identificationAllowed &&
            (configResolver.qualification?.enrich_all_visitors === true ||
                conversationPairs.length > 0);

        // Load Snitcher Radar SDK. Kept at the RoseWidget level (not
        // ExpandedChatView) so it boots as soon as identification is allowed,
        // before the user sends their next message from collapsed view.
        useSnitcherRadar({ apiUrl: effectiveApiUrl, enabled: shouldIdentifyVisitor });

        // Load Vector.co pixel when staff has enabled it for this site, subject
        // to the same engagement gate as Snitcher above.
        // distinctId + siteDomain + apiUrl enable the real-time link path:
        // useVector tags Vector with the PostHog distinct_id and forwards
        // pixel-emitted visitor.id events to /account-context/id-link
        // so the batch webhook can join on visitors.vector_visitor_id.
        const vectorEnabled =
            configResolver.integrations?.vector?.enabled === true && shouldIdentifyVisitor;
        const vectorDistinctId = useMemo(() => {
            if (!vectorEnabled || !isAnalyticsReady) {
                return null;
            }
            try {
                return getStableClientId();
            } catch (error) {
                logger.debug('[Vector] PostHog distinct_id unavailable, skipping link path', error);
                return null;
            }
        }, [vectorEnabled, isAnalyticsReady]);
        useVector({
            enabled: vectorEnabled,
            partnerId: null,
            distinctId: vectorDistinctId,
            siteDomain: domain,
            apiUrl: effectiveApiUrl,
        });

        // Debug conversation language prop
        useEffect(() => {
            logger.debug('🗣️ InboundXChat conversationLanguage prop changed:', conversationLanguage);
        }, [conversationLanguage]);

        // Propagate initial sessionId to analytics once initialized
        // This ensures localStorage persistence works even when analytics initializes after useServiceInitialization
        useEffect(() => {
            if (currentSessionId && isInitialized) {
                logger.debug('📊 Propagating initial sessionId to analytics:', currentSessionId);
                setAnalyticsContext({ sessionId: currentSessionId });
            }
        }, [currentSessionId, isInitialized]);

        // Signal widget readiness to external integrations (AI Sections, etc.)
        // This sets up window flags and dispatches events that useWidgetReadiness hook listens for
        useEffect(() => {
            if (isInitialized && currentSessionId) {
                // Set window flag for polling-based detection
                window.__InboundXWidgetReady = true;

                // Dispatch event for event-based detection
                window.dispatchEvent(new CustomEvent('inboundx_widget_ready'));
                window.dispatchEvent(new CustomEvent('roseWidgetReady'));

                logger.debug('RoseWidget: Signaled readiness to external integrations');

                // Cleanup on unmount
                return () => {
                    window.__InboundXWidgetReady = false;
                };
            }
        }, [isInitialized, currentSessionId]);

        // Create ref for evaluation popup resolve function
        const evaluationPopupResolveRef = useRef<((result: EvaluationPopupResult) => void) | null>(
            null
        );

        // Handle evaluation logic
        const {
            showEvaluationPopup,
            currentEvaluationData,
            setShowEvaluationPopup,
            setCurrentEvaluationData,
            handleEvaluation,
            handleEvaluationPopupClose,
            updateMessageEvaluation: _updateMessageEvaluation,
        } = useEvaluation({
            evaluationService,
            evaluationEnabled,
            currentSessionId,
            domain,
            evaluationPopupResolveRef,
            submitEvaluationOverride,
        });

        // Handle evaluation result and update UI
        const handleEvaluationComplete = (
            messageId: string,
            evaluationRating: EvaluationRating
        ) => {
            setState((prev) => ({
                ...prev,
                conversationPairs: prev.conversationPairs.map((pair) => {
                    if (pair.answer && pair.answer.id === messageId) {
                        return {
                            ...pair,
                            answer: {
                                ...pair.answer,
                                evaluationRating,
                                isEvaluated: true,
                            },
                        };
                    }
                    return pair;
                }),
            }));
        };

        // Enhanced evaluation handler that updates UI
        const handleEvaluationWithUI = async (
            messageId: string,
            messageContent: string,
            rating: 'thumbs_up' | 'thumbs_down',
            result?: {
                sessionId?: string;
                threadId?: string;
                userMessageContent?: string;
            }
        ) => {
            const evaluationResult = await handleEvaluation(
                messageId,
                messageContent,
                rating,
                result
            );
            if (evaluationResult?.success && evaluationResult.rating) {
                handleEvaluationComplete(messageId, evaluationResult.rating);
            }
        };

        const markSessionActiveForAttribution = useCallback(
            (sessionId: string | null | undefined) => {
                const activeSessionId = sessionId?.trim();
                if (!activeSessionId) return;

                const now = Date.now();
                setAnalyticsContext({
                    lastActiveSessionId: activeSessionId,
                    lastActiveSessionDate: now,
                    conversation_triggered_by: conversationTriggeredByRef.current || 'user_typed',
                });
                updateCurrentPageRoseAttribution({
                    domain,
                    visitorId: getAnalyticsContext().clientId ?? roseStorage.getAnalyticsClientId(),
                    lastActiveSessionId: activeSessionId,
                });
            },
            [domain]
        );

        // Handle streaming messages
        const { streamingMessage, handleStreamingQuery, cancelStream } = useStreamingMessage({
            chatService: chatService instanceof InboundXService ? chatService : null,
            currentSessionId,
            evaluationEnabled,
            onEvaluate: handleEvaluationWithUI,
            onTrackConversation: (
                query,
                output,
                messageId,
                sessionId,
                threadId,
                sources,
                streamingVisitorProfile,
                streamingBookingState
            ) => {
                const messageSource = currentMessageSourceRef.current;
                logger.debug(
                    '🎯 Evaluation onTrackConversation called, using messageSource from ref:',
                    messageSource
                );
                trackConversation(
                    conversationPairs,
                    query,
                    output,
                    messageId,
                    sessionId,
                    threadId,
                    sources,
                    messageSource,
                    streamingVisitorProfile
                );

                // Update visitor profile in conversation flow state
                if (streamingVisitorProfile) {
                    logger.debug('📧 [RoseWidget] Updating visitor profile from completion', {
                        hasEmail: !!streamingVisitorProfile.email,
                    });
                    flowActions.updateVisitorProfile(streamingVisitorProfile);
                }

                // Confirm booking response received from backend (enables redirect).
                // Guard with isBookingRef: the backend sends stale booking_state from
                // LangGraph checkpoint even on non-booking turns, so only act when the
                // frontend is actually in BOOKING flow.
                if (streamingBookingState?.is_booking_flow && isBookingRef.current) {
                    // Update redirect URL if backend built a full URL with custom fields
                    if (streamingBookingState.redirect_url) {
                        flowActions.updateBookingRedirectUrl(streamingBookingState.redirect_url);
                    }

                    // Only confirm (enable redirect) when all booking fields are collected.
                    // pending_booking_fields is empty when the backend is ready to redirect.
                    const pendingFields = streamingBookingState.pending_booking_fields ?? [];
                    flowActions.updatePendingBookingFields(pendingFields);
                    if (pendingFields.length === 0) {
                        logger.debug(
                            '📧 [RoseWidget] All booking fields collected, enabling redirect'
                        );
                        flowActions.confirmBookingResponse();
                    } else {
                        logger.debug('📧 [RoseWidget] Booking fields still pending', {
                            pendingFields,
                        });
                    }
                }

                // Exit booking state when backend says booking flow ended
                if (streamingBookingState && !streamingBookingState.is_booking_flow) {
                    logger.debug('📧 [RoseWidget] Backend ended booking flow, cancelling');
                    flowActions.cancelBookingFlow();
                }
            },
            onUpdateSessionId: (sessionId) => {
                setState((prev) => ({ ...prev, currentSessionId: sessionId }));
                // Update analytics context so the event bus updates localStorage
                setAnalyticsContext({ sessionId });
                markSessionActiveForAttribution(sessionId);
            },
        });

        // Compute combined busy state: loading OR streaming in progress
        // This prevents sending new messages while response is still streaming
        const isBusy = useMemo(() => {
            return (
                widgetState.isLoading || (streamingMessage !== null && !streamingMessage.isComplete)
            );
        }, [widgetState.isLoading, streamingMessage]);

        // Scroll functions now come from useScrollBehavior hook (defined above)

        // Handle mouse hover tracking (first time only)
        const handleMouseEnter = useCallback(() => {
            if (
                !widgetState.hasTrackedHover &&
                isAnalyticsReady &&
                currentSessionId &&
                isInitialized
            ) {
                logger.debug('📊 Tracking mouse hover - first time hover detected');
                const currentWidgetState = getState();
                track(
                    'mouse_hover',
                    {
                        widgetState: currentWidgetState,
                    },
                    'user_action'
                );
                widgetActions.trackHover();
            }
        }, [
            widgetState.hasTrackedHover,
            isAnalyticsReady,
            currentSessionId,
            isInitialized,
            getState,
            widgetActions,
        ]);

        // Setup evaluation popup callback when services are ready
        useEffect(() => {
            if (evaluationService) {
                setupEvaluationPopupCallback(
                    setCurrentEvaluationData,
                    setShowEvaluationPopup,
                    evaluationPopupResolveRef
                );
            }
        }, [
            evaluationService,
            setupEvaluationPopupCallback,
            setCurrentEvaluationData,
            setShowEvaluationPopup,
        ]);

        // Analytics system is initialized in main.tsx and should always be ready
        // The event bus handles provider availability internally
        // Note: AnalyticsContext now guarantees PostHog distinct_id is available when isAnalyticsReady is true

        // Set up analytics context when component initializes
        // Note: PostHog initialization tracking is now handled in widget.tsx before traffic control
        useEffect(() => {
            if (isInitialized && currentSessionId && domain && isAnalyticsReady) {
                logger.debug('🎯 Setting analytics context (event bus will update localStorage)');
                const deploymentTarget = getDeploymentTarget();

                // Restore lastActiveSessionId from subdomain-scoped localStorage first.
                // On a fresh visit to a different subdomain (e.g. checkout.augment.org after
                // augment.org), localStorage is empty — fall back to the cross-subdomain
                // cookies so attribution is seeded before any event is emitted.
                const storedLastActiveSessionId = roseStorage.getLastActiveSessionId();
                const storedLastActiveSessionDate = roseStorage.getLastActiveSessionDate();
                const crossDomainData = !storedLastActiveSessionId
                    ? getCrossDomainTrackingData()
                    : null;
                const effectiveLastActiveSessionId =
                    storedLastActiveSessionId || crossDomainData?.lastActiveSessionId || undefined;
                const effectiveLastActiveSessionDate =
                    storedLastActiveSessionDate ||
                    crossDomainData?.lastActiveSessionDate ||
                    undefined;

                // Get stable clientId (same source of truth as traffic control)
                // This ensures analytics and traffic control use the same clientId
                // STRICT MODE: getStableClientId automatically fetches PostHog to use distinct_id
                // Since isAnalyticsReady guarantees PostHog is ready, this should never fail
                const clientId = getStableClientId();

                // Update analytics context - event bus handles localStorage storage
                setAnalyticsContext({
                    domain,
                    sessionId: currentSessionId,
                    lastActiveSessionId: effectiveLastActiveSessionId,
                    lastActiveSessionDate: effectiveLastActiveSessionDate,
                    clientId,
                    deploymentTarget,
                    // apiVersion, environment, frontendVersion, gitCommitSha already set in AnalyticsContext
                });
                updateCurrentPageRoseAttribution({
                    domain,
                    visitorId: clientId,
                });

                logger.debug('📊 Analytics context initialized:', {
                    sessionId: currentSessionId,
                    lastActiveSessionId: effectiveLastActiveSessionId,
                    lastActiveSessionDate: effectiveLastActiveSessionDate,
                    clientId,
                    seededFromCookies:
                        !storedLastActiveSessionId && !!crossDomainData?.lastActiveSessionId,
                });
            }
        }, [isInitialized, currentSessionId, domain, isAnalyticsReady]);

        // Save state whenever it changes. Not once routing has moved the host to another
        // workspace: this transcript belongs to the previous one, and re-initialization is
        // about to restore the new workspace's own.
        useEffect(() => {
            if (isInitialized && currentSessionId && conversationScope(domain) === storageScope) {
                const state = {
                    conversationPairs,
                    isExpanded,
                    isExplicitlyMinimized,
                    viewLayout: effectiveViewLayout,
                    sessionId: currentSessionId,
                    siteName: domain,
                };
                ChatStorageService.saveChatState(domain, state);
            }
        }, [
            conversationPairs,
            isExpanded,
            isExplicitlyMinimized,
            effectiveViewLayout,
            currentSessionId,
            domain,
            isInitialized,
            storageScope,
        ]);

        // Auto-scroll to latest question when new messages arrive
        // Wait for follow-up question animations to complete before scrolling
        // (max stagger delay 0.4s + animation duration 0.5s + buffer)
        useEffect(() => {
            if (conversationPairs.length > 0 && isExpanded) {
                setTimeout(() => {
                    if (latestQuestionRef.current) {
                        scrollToLatestQuestion();
                    } else {
                        scrollToBottom();
                    }
                }, 950);
            }
        }, [
            conversationPairs.length,
            isExpanded,
            scrollToLatestQuestion,
            scrollToBottom,
            latestQuestionRef,
        ]);

        // Prevent expanded state when there are no conversation pairs
        // Skip this check during retry to prevent premature collapse when removing failed message
        useEffect(() => {
            if (
                isExpanded &&
                conversationPairs.length === 0 &&
                isInitialized &&
                !isRetryingRef.current
            ) {
                logger.debug('InboundXChat: No conversation pairs but expanded, collapsing chat');
                widgetActions.collapse();
                setState((prev) => ({
                    ...prev,
                    isExpanded: false,
                    isExplicitlyMinimized: false,
                }));
                ChatStorageService.setExpanded(domain, false);
            }
        }, [isExpanded, conversationPairs.length, domain, isInitialized, setState, widgetActions]);

        // Set initial minimized state for form assistant in copilot mode (only on first load, no messages)
        // In copilot mode with form assistant, we start with the minimized view (small button) instead of collapsed (search bar)
        // Exception: page_display_rules with 'displayed' override copilot minimized — the user explicitly
        // configured this page to show the widget normally.
        useEffect(() => {
            // Check page display rules inline (pageRuleDisplay useMemo is declared below)
            const pageRules = configResolver.traffic_control.page_display_rules;
            const url =
                currentUrlProp || (typeof window !== 'undefined' ? window.location.href : '');
            const currentPageRule = isForcedBackofficePreview
                ? 'displayed'
                : pageRules?.length
                  ? (resolvePageDisplayRule(pageRules, url)?.display ?? null)
                  : null;

            if (
                formFactor === 'copilot' &&
                isFormAssistant &&
                isInitialized &&
                !hasAppliedCopilotInitialState.current &&
                conversationPairs.length === 0 &&
                !isExpanded &&
                !isExplicitlyMinimized &&
                currentPageRule !== 'displayed'
            ) {
                hasAppliedCopilotInitialState.current = true;
                logger.debug('🎯 Form assistant: setting initial state to minimized');
                setState((prev) => ({ ...prev, isExplicitlyMinimized: true }));
                // Note: Don't persist to storage - this is just the initial view mode
            }
        }, [
            formFactor,
            isFormAssistant,
            isInitialized,
            conversationPairs.length,
            isExpanded,
            isExplicitlyMinimized,
            setState,
            configResolver,
            currentUrlProp,
            isForcedBackofficePreview,
        ]);

        // Post-conversion auto-open: on thank-you pages, auto-expand after 1s with welcome message
        useEffect(() => {
            if (
                !isPostConversion ||
                !isInitialized ||
                hasAppliedPostConversionAutoOpen.current ||
                conversationPairs.length > 0
            ) {
                return;
            }

            // Check if qualification was already completed (persists across sessions)
            if (ChatStorageService.isPostConversionComplete(domain)) {
                logger.debug('🎯 Post-conversion: qualification already completed, skipping');
                hasAppliedPostConversionAutoOpen.current = true;
                return;
            }

            // Check sessionStorage dismissal (domain-scoped, cleared on tab close)
            const dismissalKey = `rose_pcq_dismissed_${domain}`;
            if (typeof window !== 'undefined' && window.sessionStorage.getItem(dismissalKey)) {
                logger.debug('🎯 Post-conversion: auto-open dismissed for this session');
                hasAppliedPostConversionAutoOpen.current = true;
                return;
            }

            hasAppliedPostConversionAutoOpen.current = true;

            const timer = setTimeout(() => {
                // Create a placeholder pair to keep the widget expanded.
                // The question (POST_CONVERSION_WELCOME_MARKER) is hidden by ConversationPair.
                // The answer will be streamed from the backend (welcome + first question).
                const placeholderQuestion = createUserMessage(
                    Date.now().toString(),
                    POST_CONVERSION_WELCOME_MARKER,
                    Date.now()
                );
                const placeholderPair = createConversationPair(placeholderQuestion);

                widgetActions.expand();
                setState((prev) => ({
                    ...prev,
                    conversationPairs: [placeholderPair],
                    isExpanded: true,
                    isExplicitlyMinimized: false,
                }));
                ChatStorageService.setExpanded(domain, true);
                logger.debug('🎯 Post-conversion: auto-opened widget with placeholder pair');

                // Track post-qualification funnel start
                const activeForm = getActivePostConversionForm(domain);
                const totalQuestions = activeForm?.config?.field_groups
                    ? (activeForm.config.field_groups as Array<{ questions?: unknown[] }>).reduce(
                          (sum: number, g: { questions?: unknown[] }) =>
                              sum + (g.questions?.length ?? 0),
                          0
                      )
                    : 0;
                Analytics.trackPostQualificationStarted({
                    totalQuestions,
                    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
                });

                // Send hidden message to backend — it responds with welcome + first question
                setTimeout(() => {
                    if (handleSendMessageRef.current) {
                        handleSendMessageRef.current(
                            POST_CONVERSION_WELCOME_MARKER,
                            'post_conversion_trigger'
                        );
                        logger.debug('🎯 Post-conversion: sent hidden placeholder to backend');
                    }
                }, 300);
            }, 1000);

            return () => clearTimeout(timer);
        }, [
            isPostConversion,
            isInitialized,
            conversationPairs.length,
            domain,
            widgetActions,
            setState,
        ]);

        // Resolve page_display_rules for current URL (synchronous — no flash)
        // RoseWidget owns this logic so it's not duplicated between DisplayController and chrome extension.
        // - 'hidden' → widget returns null (hidden for this URL)
        // - 'minimized' → show MinimizedChatView (visible small button)
        // - 'displayed'/no match → normal widget behavior
        const pageRuleDisplay = useMemo(() => {
            if (isForcedBackofficePreview) return null;
            const pageRules = configResolver.traffic_control.page_display_rules;
            if (!pageRules?.length) return null;
            const url =
                currentUrlProp || (typeof window !== 'undefined' ? window.location.href : '');
            const matchedRule = resolvePageDisplayRule(pageRules, url);
            return matchedRule?.display ?? null;
        }, [configResolver, currentUrlProp, isForcedBackofficePreview]);

        // Sync minimized state when page rule changes (side effect → useEffect)
        // Apply minimized/displayed ONCE per URL navigation — user can still expand/collapse freely.
        // On navigation away from a minimized-rule page, reset minimized state.
        // 'displayed' rule overrides any minimized state (including copilot-sourced) once per URL.
        useEffect(() => {
            if (!isInitialized) return;
            const currentUrl = currentUrlProp || '';

            if (pageRuleDisplay === 'minimized' && pageRuleAppliedForUrl.current !== currentUrl) {
                // Apply minimized once for this URL — only mark as applied when the
                // state change actually lands. Otherwise a concurrent state reset
                // (e.g. the "no conversation pairs but expanded" cleanup effect)
                // would race ahead of this effect, leave isExpanded=true on first
                // pass so the inner branch skips, and then the URL would be marked
                // as already applied — permanently blocking minimized on this URL.
                if (!isExpanded && !isExplicitlyMinimized) {
                    pageRuleAppliedForUrl.current = currentUrl;
                    displayedRuleAppliedForUrl.current = null;
                    logger.debug('📐 Page display rule: setting state to minimized');
                    setState((prev) => ({ ...prev, isExplicitlyMinimized: true }));
                }
            } else if (
                pageRuleDisplay === 'displayed' &&
                isExplicitlyMinimized &&
                !isExpanded &&
                displayedRuleAppliedForUrl.current !== currentUrl
            ) {
                // Explicit 'displayed' rule overrides minimized state (e.g., copilot mode, SPA navigation)
                // Applied once per URL — user can still minimize freely after this override.
                displayedRuleAppliedForUrl.current = currentUrl;
                pageRuleAppliedForUrl.current = null;
                logger.debug('📐 Page display rule: "displayed" overrides minimized state');
                setState((prev) => ({ ...prev, isExplicitlyMinimized: false }));
            } else if (pageRuleDisplay !== 'minimized' && pageRuleAppliedForUrl.current !== null) {
                // Navigated away from minimized-rule page — reset
                pageRuleAppliedForUrl.current = null;
                if (isExplicitlyMinimized && !isExpanded) {
                    logger.debug('📐 Page display rule: resetting minimized state');
                    setState((prev) => ({ ...prev, isExplicitlyMinimized: false }));
                }
            }
        }, [
            pageRuleDisplay,
            currentUrlProp,
            isInitialized,
            isExpanded,
            isExplicitlyMinimized,
            setState,
        ]);

        // Clear copilot-only conversation when switching to default mode
        // This ensures users don't see the copilot welcome message on non-CTA pages
        useEffect(() => {
            if (
                formFactor === 'default' &&
                isInitialized &&
                conversationPairs.length === 1 &&
                conversationPairs[0].question?.content === FORM_ASSISTANT_WELCOME_MARKER
            ) {
                logger.debug('🧹 Default mode: clearing form assistant welcome message');
                setState((prev) => ({
                    ...prev,
                    conversationPairs: [],
                    isExpanded: false,
                    isExplicitlyMinimized: false,
                }));
                ChatStorageService.clearChatState(domain);
            }
        }, [formFactor, isInitialized, conversationPairs, setState, domain]);

        // Clear default mode conversation when switching to form assistant mode
        // This ensures users get a fresh form assistant welcome message on CTA pages
        useEffect(() => {
            if (
                formFactor === 'copilot' &&
                isFormAssistant &&
                isInitialized &&
                conversationPairs.length > 0 &&
                conversationPairs[0].question?.content !== FORM_ASSISTANT_WELCOME_MARKER
            ) {
                logger.debug('🧹 Form assistant mode: clearing default mode conversation');
                setState((prev) => ({
                    ...prev,
                    conversationPairs: [],
                    isExpanded: false,
                    isExplicitlyMinimized: false,
                }));
                ChatStorageService.clearChatState(domain);
            }
        }, [formFactor, isFormAssistant, isInitialized, conversationPairs, setState, domain]);

        // Clear incompatible prior conversation when entering post-conversion mode.
        // Keep the current in-memory session so the backend still has prior context.
        useEffect(() => {
            if (
                !isPostConversion ||
                !isInitialized ||
                !shouldResetConversationForPostConversion(conversationPairs)
            ) {
                return;
            }

            logger.debug('🧹 Post-conversion mode: clearing incompatible prior conversation');
            hasAppliedPostConversionAutoOpen.current = false;
            setState((prev) => ({
                ...prev,
                conversationPairs: [],
                isExpanded: false,
                isExplicitlyMinimized: false,
            }));
            ChatStorageService.clearChatState(domain);
        }, [isPostConversion, isInitialized, conversationPairs, setState, domain]);

        // Keep latestProfilingProgressRef in sync so dismiss handler can read it.
        // When qualification completes, persist to localStorage so the widget
        // won't re-open the post-conversion flow on future visits.
        useEffect(() => {
            if (!isPostConversion) return;
            const lastPair = conversationPairs[conversationPairs.length - 1];
            const progress = lastPair?.answer?.profilingProgress;
            if (progress) {
                latestProfilingProgressRef.current = progress;
                if (progress.is_complete) {
                    ChatStorageService.setPostConversionComplete(domain);
                }
            }
        }, [isPostConversion, conversationPairs, domain]);

        // Helper function to update the last conversation pair with an answer
        const updateLastConversationPairAnswer = useCallback(
            (answer: AssistantMessage) => {
                setState((prev) => {
                    const updatedPairs = [...prev.conversationPairs];
                    const lastPairIndex = updatedPairs.length - 1;
                    if (lastPairIndex >= 0) {
                        updatedPairs[lastPairIndex] = {
                            ...updatedPairs[lastPairIndex],
                            answer,
                        };
                    }
                    return {
                        ...prev,
                        conversationPairs: updatedPairs,
                    };
                });
            },
            [setState]
        );

        const handleToggleLayout = useCallback(() => {
            if (isFormAssistant || formFactor === 'copilot' || !enableCopilotLayout) return;
            widgetActions.switchLayout();
            const newLayout = effectiveViewLayout === 'expanded' ? 'copilot' : 'expanded';
            ChatStorageService.setViewLayout(domain, newLayout);
            // IX-4086: not tracked here - ExpandedChatView also calls this
            // programmatically (in-chat embed needs the full-width layout), so the
            // click event is emitted at the toggle button instead.
        }, [
            domain,
            effectiveViewLayout,
            enableCopilotLayout,
            formFactor,
            isFormAssistant,
            widgetActions,
        ]);

        // Exit copilot mode (e.g. user toggles back to expanded layout).
        const handleExitCopilotMode = useCallback(() => {
            if (isFormAssistant) return;
            setFormFactorOverride('default');
            widgetActions.setViewLayout('expanded');
            ChatStorageService.setViewLayout(domain, 'expanded');
            ChatStorageService.setCopilotModeOverride(configResolver.domain, false);
            // IX-4086: leaving copilot *mode* (form factor), distinct from the
            // expanded/copilot layout toggle tracked in ExpandedChatView.
            track('copilot_mode_exit_clicked', { siteName }, 'user_action');
        }, [configResolver.domain, domain, isFormAssistant, siteName, widgetActions]);

        // Memoize handleResetConversation to avoid dependency issues
        const handleResetConversation = useCallback(async () => {
            if (chatService) {
                // A second reset while the first is in flight would rotate the
                // session twice and could wipe a message the visitor started
                // after the first one landed.
                if (isResettingRef.current) return;
                isResettingRef.current = true;

                // Abandon any in-flight answer first: its tokens belong to the
                // session we are about to discard, and leaving it running keeps
                // the new input blocked until the stream ends or times out.
                cancelStream();

                try {
                    const success = await chatService.resetMemory();

                    if (success) {
                        const newSessionId = chatService.getSessionId() || '';

                        setState((prev) => ({
                            ...prev,
                            conversationPairs: [],
                            isExpanded: false,
                            isExplicitlyMinimized: false,
                            currentSessionId: newSessionId,
                        }));
                        ChatStorageService.clearChatState(domain);
                        userMessageCountRef.current = 0; // Reset message count
                        conversationTriggeredByRef.current = null; // Reset conversation trigger

                        // Update analytics context with new session ID and clear conversation_triggered_by
                        setAnalyticsContext({
                            sessionId: newSessionId,
                            conversation_triggered_by: null,
                        });

                        logger.debug(
                            'InboundXChat: Conversation reset successfully with new sessionId:',
                            newSessionId
                        );
                    } else {
                        logger.error('InboundXChat: Failed to reset conversation on server');
                    }
                } finally {
                    isResettingRef.current = false;
                }
            }
        }, [chatService, setState, domain, cancelStream]);

        // Expose methods to parent component via ref
        useImperativeHandle(
            ref,
            () => ({
                collapse: () => {
                    // Set to collapsed state (initial state with search bar)
                    widgetActions.collapse();
                    ChatStorageService.setExpanded(domain, false);
                },
                expand: () => {
                    // Set to expanded state (full chat view)
                    widgetActions.expand();
                    ChatStorageService.setExpanded(domain, true);
                },
                minimize: () => {
                    // Set to minimized state (user explicitly minimized conversation)
                    widgetActions.minimize();
                    ChatStorageService.setExpanded(domain, false);
                },
                reset: () => {
                    // Reset conversation and return to collapsed state
                    handleResetConversation();
                },
                getState,
                isExpanded: widgetState.isExpanded,
                isExplicitlyMinimized: widgetState.isExplicitlyMinimized,
            }),
            [
                widgetActions,
                widgetState.isExpanded,
                widgetState.isExplicitlyMinimized,
                getState,
                domain,
                handleResetConversation,
            ]
        );

        // Listen for external question triggers (from AI Sections or other integrations)
        // This useEffect MUST be before conditional returns to follow Rules of Hooks
        useEffect(() => {
            const handleTriggerQuestion = (
                event: CustomEvent<{ question: string; source?: string; context?: string }>
            ) => {
                const { question, source = 'ai_section', context } = event.detail || {};

                if (!question || typeof question !== 'string' || !question.trim()) {
                    logger.warn('RoseWidget: Received invalid trigger question event');
                    return;
                }

                // Check if widget is ready to handle the question
                if (!handleSendMessageRef.current) {
                    logger.warn('RoseWidget: Widget not ready to handle trigger question');
                    return;
                }

                logger.debug('RoseWidget: Received external question trigger', {
                    question,
                    source,
                });

                // Expand the widget and send the question
                // Clear isExplicitlyMinimized to maintain consistent persisted state
                widgetActions.expand();
                setState((prev) => ({
                    ...prev,
                    isExpanded: true,
                    isExplicitlyMinimized: false,
                }));
                ChatStorageService.setExpanded(domain, true);

                // Send the message with the appropriate source for analytics tracking
                const aiSectionSources = [
                    'ai_section_button',
                    'ai_section_search_bar',
                    'ai_section',
                ];
                const messageSource = aiSectionSources.includes(source) ? source : 'user_typed';
                prepareVisitorIdentification();
                handleSendMessageRef.current(
                    question.trim(),
                    messageSource as MessageSourceType,
                    undefined,
                    context
                );
            };

            window.addEventListener(
                'inboundx_trigger_question',
                handleTriggerQuestion as EventListener
            );

            return () => {
                window.removeEventListener(
                    'inboundx_trigger_question',
                    handleTriggerQuestion as EventListener
                );
            };
        }, [widgetActions, setState, domain, prepareVisitorIdentification]);

        // Let host actions (e.g. opening the Intercom messenger) minimize the widget so it does
        // not overlap the surface the visitor is being handed off to.
        useEffect(() => {
            const handleMinimize = () => {
                logger.debug('RoseWidget: Received external minimize request');
                // Update BOTH stores: the reducer and the render-driving service state
                // (mirrors the trigger-question handler above) — otherwise the view never
                // re-renders to the minimized state.
                widgetActions.minimize();
                setState((prev) => ({ ...prev, isExpanded: false, isExplicitlyMinimized: true }));
                ChatStorageService.setExpanded(domain, false);
            };
            window.addEventListener('inboundx_minimize', handleMinimize);
            return () => window.removeEventListener('inboundx_minimize', handleMinimize);
        }, [widgetActions, setState, domain]);

        const handleSendMessage = async (
            messageToSend: string,
            messageSource?: MessageSourceType,
            bookingContext?: BookingContext,
            questionContext?: string
        ) => {
            if (!messageToSend.trim() || !chatService || isBusy) return;

            // Store the message source in ref so it can be accessed by the streaming callback
            currentMessageSourceRef.current = messageSource || 'user_typed';

            // If this is a programmatic call (like from dynamic question or AI section), set the flag to prevent double calls
            if (messageSource === 'dynamic_question' || messageSource === 'ai_section') {
                setIsProgrammaticallySending(true);
            }

            // Prevent double submission by checking if we're already processing this exact message
            // Skip for post-conversion triggers (placeholder pair already has the marker as question)
            if (!isRetryingRef.current && messageSource !== 'post_conversion_trigger') {
                const lastPair = conversationPairs[conversationPairs.length - 1];
                if (lastPair && lastPair.question.content === messageToSend.trim()) {
                    return;
                }
            }

            // Increment user message count and track message sent event
            userMessageCountRef.current += 1;

            // Set conversation_triggered_by on first message
            if (userMessageCountRef.current === 1) {
                conversationTriggeredByRef.current = messageSource || 'user_typed';
            }

            // Track message sent event
            // Fire and forget - don't wait for tracking to complete
            track(
                'message_sent',
                {
                    messageNumber: userMessageCountRef.current,
                    messageLength: messageToSend.length,
                    message_source: messageSource || 'user_typed', // Track whether message was typed or from dynamic question
                    conversation_triggered_by: conversationTriggeredByRef.current || undefined, // Track how conversation was initially triggered
                    is_post_conversion: isPostConversion || false,
                    is_form_assistant: isFormAssistant || false,
                },
                'user_action'
            );

            // Track post-qualification answer (skip the hidden welcome trigger)
            if (isPostConversion && messageSource !== 'post_conversion_trigger') {
                const progress = latestProfilingProgressRef.current;
                Analytics.trackPostQualificationAnswerSubmitted({
                    questionNumber: progress?.current_question ?? 0,
                    totalQuestions: progress?.total_fields ?? 0,
                    answerSource:
                        messageSource === 'suggested_answer' ? 'suggested_answer' : 'user_typed',
                });
            }

            // Mark the session active after every accepted outbound message. Prefer React state,
            // but fall back to the service because first sends can race state propagation.
            markSessionActiveForAttribution(
                currentSessionId ||
                    (chatService instanceof InboundXService ? chatService.getSessionId() : null)
            );

            // For post-conversion triggers, a placeholder pair already exists — skip creating a new one.
            // The backend response will stream into the existing pair's answer slot.
            if (messageSource !== 'post_conversion_trigger') {
                const userMessage = createUserMessage(
                    Date.now().toString(),
                    messageToSend,
                    Date.now()
                );
                const newConversationPair = createConversationPair(userMessage);
                setState((prev) => ({
                    ...prev,
                    conversationPairs: [...prev.conversationPairs, newConversationPair],
                }));
            }

            widgetActions.sendMessageStart();

            // Track whether sendMessageSuccess was already called in a streaming callback.
            // This prevents a race condition where the finally block would reset isLoading
            // for a NEW message that started between the callback and the finally block.
            let loadingCleared = false;
            const clearLoading = () => {
                if (!loadingCleared) {
                    loadingCleared = true;
                    widgetActions.sendMessageSuccess();
                }
            };

            try {
                // Use streaming if enabled and service supports it
                if (streamingEnabled && chatService instanceof InboundXService) {
                    await handleStreamingQuery(
                        messageToSend,
                        (botMessage) => {
                            if (isAssistantMessage(botMessage)) {
                                updateLastConversationPairAnswer(botMessage);
                            }
                            // Mark loading as done when the response is logically complete,
                            // not when the network stream closes. This prevents a race condition
                            // where the demo CTA button renders (streamingMessage=null) but
                            // isBusy is still true, silently dropping booking flow messages.
                            clearLoading();
                        },
                        (errorMessage) => {
                            if (isAssistantMessage(errorMessage)) {
                                updateLastConversationPairAnswer(errorMessage);
                            }
                            clearLoading();
                        },
                        messageSource,
                        bookingContext,
                        isFormAssistant,
                        questionContext,
                        isPostConversion
                    );
                } else {
                    const result = await chatService.query(
                        messageToSend,
                        messageSource,
                        isFormAssistant,
                        isPostConversion
                    );

                    // Update session ID if it changed
                    if (result.sessionId && result.sessionId !== currentSessionId) {
                        setState((prev) => ({
                            ...prev,
                            currentSessionId: result.sessionId || '',
                        }));
                        // Update analytics context so the event bus updates localStorage
                        setAnalyticsContext({ sessionId: result.sessionId });
                        markSessionActiveForAttribution(result.sessionId);
                    }

                    const messageId = result.messageId || (Date.now() + 1).toString();
                    const userLanguage = getUserLanguage(conversationLanguage, undefined, domain);
                    const content = result.error
                        ? getMessage('network_error', userLanguage)
                        : result.content || getMessage('generic_error', userLanguage);

                    const botMessage = createAssistantMessage(messageId, content, {
                        timestamp: Date.now(),
                        isEvaluated: false,
                        isError: !!result.error, // Mark as error if there's an error field
                        onEvaluate: evaluationEnabled
                            ? (rating: 'thumbs_up' | 'thumbs_down') => {
                                  handleEvaluationWithUI(messageId, content, rating, {
                                      ...result,
                                      userMessageContent: messageToSend,
                                  });
                              }
                            : undefined,
                    });

                    updateLastConversationPairAnswer(botMessage);

                    // Track conversation
                    logger.debug(
                        '🎯 About to call trackConversation with messageSource:',
                        messageSource
                    );
                    trackConversation(
                        conversationPairs,
                        messageToSend,
                        botMessage.content,
                        botMessage.id,
                        result.sessionId,
                        (result as { sessionId?: string; threadId?: string })?.threadId,
                        undefined,
                        messageSource,
                        result.visitor_profile
                    );
                }
            } catch (error) {
                logger.error('InboundXChat: Error during handleSendMessage', error);
                const errorMessage = createAssistantMessage(
                    (Date.now() + 1).toString(),
                    getMessage(
                        'generic_error',
                        getUserLanguage(conversationLanguage, undefined, domain)
                    ),
                    {
                        timestamp: Date.now(),
                        isError: true,
                    }
                );
                updateLastConversationPairAnswer(errorMessage);
                logger.error('InboundXChat: Error processing message:', errorMessage);
            } finally {
                clearLoading();
                // Reset the programmatic sending flag
                setIsProgrammaticallySending(false);
            }
        };

        // Update ref so the useEffect (defined before conditional returns) can access handleSendMessage
        handleSendMessageRef.current = handleSendMessage;

        // Handle retry - resend the last user message
        const handleRetry = async () => {
            // Find the last conversation pair with a user message
            const lastPair = conversationPairs[conversationPairs.length - 1];
            if (!lastPair?.question) return;

            const messageToRetry = lastPair.question.content;

            // Set retry flag BEFORE removing pair to:
            // 1. Bypass duplicate message check in handleSendMessage
            // 2. Prevent auto-collapse effect from minimizing widget when conversationPairs becomes empty
            isRetryingRef.current = true;

            // Remove the failed conversation pair
            setState((prev) => ({
                ...prev,
                conversationPairs: prev.conversationPairs.slice(0, -1),
            }));

            // Wait for next tick to ensure state update is processed
            await new Promise((resolve) => setTimeout(resolve, 0));

            // Now resend the message and reset flag when done
            try {
                await handleSendMessage(messageToRetry);
            } finally {
                // Reset retry flag after message is processed
                isRetryingRef.current = false;
            }
        };

        // handleKeyPress is now handled inside SearchBar component

        // Don't render the widget if API is not reachable or version incompatible (unless forceDisplay is true)
        // Note: Root cause (API failure) was already logged by useServiceInitialization - use debug here
        if (!isApiCompatibleAndAvailable && !forceDisplay) {
            logger.debug(
                'InboundXChat: Widget not displayed - API not reachable or version incompatible'
            );
            return null;
        }

        // Special handling for forceDisplay mode when API is not reachable
        if (!isApiCompatibleAndAvailable && forceDisplay) {
            logger.debug(
                'InboundXChat: API not reachable, but forceDisplay is enabled. Widget will render anyway.'
            );
        }

        // Note: CTA page hiding logic has been moved to DisplayController.
        // When form_assistant feature flag is enabled, CTA pages show copilot mode instead of hiding.
        // When form_assistant is disabled, shouldUseCopilotMode returns false and DisplayController
        // uses the default form factor - the old hide behavior is now handled by the feature flag.

        // Render content based on state - wrapped in single HiddenZoneWidget
        let widgetContent: React.ReactNode;

        if (!isExpanded) {
            // Show MinimizedChatView only if user explicitly minimized from a conversation
            // Otherwise show CollapsedChatView (search bar) for initial state

            if (isExplicitlyMinimized) {
                widgetContent = (
                    <div className="inboundx-chat-container">
                        <MinimizedChatView
                            onClick={() => {
                                prepareVisitorIdentification();
                                // IX-4086: clicking the minimized bubble either reopens the
                                // conversation or, with nothing to reopen, dismisses it back
                                // to the search bar.
                                const reopens =
                                    conversationPairs.length > 0 ||
                                    (formFactor === 'copilot' && isFormAssistant);
                                track(
                                    reopens ? 'widget_expand_clicked' : 'widget_close_clicked',
                                    {
                                        siteName,
                                        messageCount: conversationPairs.length,
                                        viewLayout: effectiveViewLayout,
                                    },
                                    'user_action'
                                );
                                // If there are messages, expand to full view
                                if (conversationPairs.length > 0) {
                                    widgetActions.expand();
                                    setState((prev) => ({
                                        ...prev,
                                        isExpanded: true,
                                        isExplicitlyMinimized: false,
                                    }));
                                    ChatStorageService.setExpanded(domain, true);
                                    setTimeout(() => {
                                        if (latestQuestionRef.current) {
                                            scrollToLatestQuestion();
                                        } else {
                                            scrollToBottom();
                                        }
                                    }, 200);
                                } else if (formFactor === 'copilot' && isFormAssistant) {
                                    // Form assistant in copilot mode with no messages: create welcome message and expand directly
                                    const currentUrl =
                                        typeof window !== 'undefined'
                                            ? window.location.href
                                            : undefined;
                                    const userLanguage = getUserLanguage(
                                        conversationLanguage,
                                        currentUrl,
                                        domain
                                    );
                                    const userLocale = getUserLocale(
                                        conversationLanguage,
                                        currentUrl,
                                        domain
                                    );
                                    const followUps = getFormAssistantQuestions(
                                        domain,
                                        userLocale,
                                        currentUrl
                                    );
                                    const welcomeMessage =
                                        getFormAssistantInitialMessage(userLanguage);

                                    // Create welcome pair with follow-ups
                                    const welcomePair = createFormAssistantWelcomePair(
                                        welcomeMessage,
                                        followUps,
                                        FORM_ASSISTANT_WELCOME_MARKER
                                    );

                                    // Update state and expand
                                    widgetActions.expand();
                                    setState((prev) => ({
                                        ...prev,
                                        conversationPairs: [welcomePair],
                                        isExpanded: true,
                                        isExplicitlyMinimized: false,
                                    }));
                                    ChatStorageService.setExpanded(domain, true);
                                } else {
                                    // Default mode with no messages: show collapsed view (search bar)
                                    widgetActions.collapse();
                                    setState((prev) => ({
                                        ...prev,
                                        isExpanded: false,
                                        isExplicitlyMinimized: false,
                                    }));
                                    ChatStorageService.setExpanded(domain, false);
                                }
                            }}
                            primaryColor={primaryColor}
                            siteConfig={siteConfig}
                            zIndex={zIndex}
                            formFactor={formFactor}
                            viewLayout={effectiveViewLayout}
                        />
                    </div>
                );
            } else {
                // Show CollapsedChatView (search bar) for initial state or when returning to URL
                widgetContent = (
                    <div className="inboundx-chat-container">
                        <CollapsedChatView
                            onSendMessage={(messageToSend, messageSource) => {
                                prepareVisitorIdentification();
                                widgetActions.expand();
                                setState((prev) => ({ ...prev, isExpanded: true }));
                                ChatStorageService.setExpanded(domain, true);
                                handleSendMessage(messageToSend, messageSource);
                            }}
                            isLoading={isBusy}
                            primaryColor={primaryColor}
                            siteConfig={siteConfig}
                            domain={domain}
                            zIndex={zIndex}
                            bypassScrollDetection={bypassScrollDetection}
                            formFactor={formFactor}
                            hiddenBySectionSearchBar={sectionSearchBarVisible}
                            onEngage={prepareVisitorIdentification}
                        />
                    </div>
                );
            }
        } else if (conversationPairs.length === 0) {
            // If expanded but no messages, show collapsed view
            widgetContent = (
                <div className="inboundx-chat-container">
                    <CollapsedChatView
                        onSendMessage={(messageToSend, messageSource) => {
                            prepareVisitorIdentification();
                            widgetActions.expand();
                            setState((prev) => ({ ...prev, isExpanded: true }));
                            ChatStorageService.setExpanded(domain, true);
                            handleSendMessage(messageToSend, messageSource);
                        }}
                        isLoading={isBusy}
                        primaryColor={primaryColor}
                        siteConfig={siteConfig}
                        domain={domain}
                        zIndex={zIndex}
                        bypassScrollDetection={bypassScrollDetection}
                        formFactor={formFactor}
                        hiddenBySectionSearchBar={sectionSearchBarVisible}
                        onEngage={prepareVisitorIdentification}
                    />
                </div>
            );
        } else {
            // Show expanded chat with messages
            widgetContent = (
                <div className="inboundx-chat-container">
                    <ExpandedChatView
                        conversationPairs={conversationPairs}
                        streamingMessage={streamingMessage || undefined}
                        onSendMessage={handleSendMessage}
                        isLoading={isBusy}
                        primaryColor={primaryColor}
                        siteConfig={siteConfig}
                        messagesEndRef={messagesEndRef}
                        onResetConversation={handleResetConversation}
                        onMinimize={() => {
                            // IX-4086: visitor minimized the expanded chat
                            track(
                                'widget_minimize_clicked',
                                {
                                    siteName,
                                    messageCount: conversationPairs.length,
                                    viewLayout: effectiveViewLayout,
                                },
                                'user_action'
                            );
                            // Mark the displayed rule as applied for this URL so the page rule
                            // effect doesn't immediately reset isExplicitlyMinimized back to false.
                            displayedRuleAppliedForUrl.current = currentUrlProp || '';
                            setState((prev) => ({
                                ...prev,
                                isExpanded: false,
                                isExplicitlyMinimized: true,
                            }));
                            ChatStorageService.setExpanded(domain, false);
                            if (isPostConversion && typeof window !== 'undefined') {
                                window.sessionStorage.setItem(
                                    `rose_pcq_dismissed_${domain}`,
                                    'true'
                                );
                                const progress = latestProfilingProgressRef.current;
                                if (progress && !progress.is_complete) {
                                    const progressPercent =
                                        progress.total_fields > 0
                                            ? Math.round(
                                                  (progress.current_question /
                                                      progress.total_fields) *
                                                      100
                                              )
                                            : 0;
                                    Analytics.trackPostQualificationDismissed({
                                        questionNumber: progress.current_question,
                                        totalQuestions: progress.total_fields,
                                        progressPercent,
                                    });
                                }
                            }
                        }}
                        evaluationEnabled={evaluationEnabled}
                        domain={domain}
                        onLatestQuestionRef={(ref) => {
                            latestQuestionRef.current = ref;
                        }}
                        zIndex={zIndex}
                        onRetry={handleRetry}
                        onEngage={prepareVisitorIdentification}
                        flowState={flowState}
                        flowActions={flowActions}
                        isFormAssistant={isFormAssistant}
                        formFactor={formFactor}
                        viewLayout={effectiveViewLayout}
                        onToggleLayout={enableCopilotLayout ? handleToggleLayout : undefined}
                        onExitCopilotMode={
                            formFactor === 'copilot' && !isFormAssistant
                                ? handleExitCopilotMode
                                : undefined
                        }
                        isPostConversion={isPostConversion}
                        previewViewportWidth={previewViewportWidth}
                    />
                </div>
            );
        }

        const evaluationPopup =
            showEvaluationPopup && currentEvaluationData ? (
                <EvaluationPopup
                    isOpen={showEvaluationPopup}
                    onClose={handleEvaluationPopupClose}
                    rating={currentEvaluationData.rating}
                    themeVars={themeVars}
                    zIndex={zIndex + 1}
                />
            ) : null;

        // Wait for analytics to initialize before rendering main content
        // This prevents child components from trying to register analytics providers before the system is ready
        if (!isAnalyticsReady) {
            logger.debug('⏸️ Waiting for analytics initialization before rendering InboundXChat');
            return null; // Return nothing while analytics initializes
        }

        // Page display rule: hidden → don't render widget for this URL
        if (pageRuleDisplay === 'hidden') {
            logger.debug('🚫 Page display rule: widget hidden for current URL');
            return null;
        }

        // Hidden zone mode: show button until user clicks
        // Page rules override hidden_zone — if a page rule matched (displayed/minimized),
        // skip hidden_zone and show the widget normally
        if (
            displayMode === 'hidden_zone' &&
            pageRuleDisplay === null &&
            !widgetState.userClickedHiddenZone
        ) {
            const button = (
                <HiddenZoneButton
                    onClick={() => {
                        logger.debug('HiddenZone: User clicked, revealing widget');
                        Analytics.trackClickHiddenZone({
                            clickMethod: 'click',
                            hiddenZoneType: 'internal',
                        });
                        widgetActions.hiddenZoneClicked();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            logger.debug(
                                'HiddenZone: User activated via keyboard, revealing widget'
                            );
                            Analytics.trackClickHiddenZone({
                                clickMethod: 'keyboard',
                                hiddenZoneType: 'internal',
                            });
                            widgetActions.hiddenZoneClicked();
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                />
            );

            // Render button outside shadow DOM if container is provided
            // This ensures proper viewport positioning with fixed positioning
            return hiddenZoneContainer ? createPortal(button, hiddenZoneContainer) : button;
        }

        // Track impression once when rendering visible widget (not HiddenZoneButton)
        // At this point all conditions are guaranteed by control flow:
        // - isAnalyticsReady checked above
        // - isApiCompatibleAndAvailable checked above
        // - displayMode checked above (only hidden_zone with no click returns early)
        // IX-2261: Gate on isInitialized && currentSessionId to ensure the session ID
        // is stable before tracking. Without this, the impression can fire before
        // useServiceInitialization completes (which may call forceNewSession()),
        // causing widget_impression to use a stale session ID.
        if (!widgetState.hasTrackedImpression && isInitialized && currentSessionId) {
            if (!hasPostHogInitializedEventBeenSent()) {
                logger.debug(
                    '⏸️ Skipping widget impression until rw_posthog_initialized has been sent'
                );
            } else {
                logger.debug('📊 Tracking widget impression - rendering visible widget', {
                    displayMode,
                    siteName,
                });
                track(
                    'widget_impression',
                    {
                        siteName,
                        displayMode,
                        deviceType: getDeviceType(),
                        userAgent:
                            typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
                    },
                    'system'
                );
                widgetActions.trackImpression();
            }
        }

        // Normal rendering (or hidden zone after click)
        return (
            <ChatThemeProvider
                theme={{
                    primaryColor,
                    siteConfig,
                    domain,
                    conversationLanguage,
                }}
            >
                {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
                <section onMouseEnter={handleMouseEnter} aria-label="Chat widget">
                    <div data-rw-theme-scope="" style={themeVars}>
                        {widgetContent}
                    </div>
                    {evaluationPopup}
                </section>
            </ChatThemeProvider>
        );
    }
);

RoseWidget.displayName = 'RoseWidget';

export default RoseWidget;
